import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { forwardRef } from 'react';
import { getUrlType } from './TAGLink.helpers';
const baseAnchorStyles = {
  color: 'inherit',
  textDecoration: 'none',
  '&:hover': {
    opacity: 0.8
  }
};
const Anchor = styled('a')({
  ...baseAnchorStyles
});
const toDisplayString = (value: NextLinkProps['href'] | NextLinkProps['as']) => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'pathname' in value && typeof value.pathname === 'string') {
    return value.pathname;
  }
  return '';
};
interface NextLinkComposedProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>, Omit<NextLinkProps, 'href' | 'as' | 'passHref' | 'onMouseEnter' | 'onClick' | 'onTouchStart'> {
  to: NextLinkProps['href'];
  linkAs?: Exclude<NextLinkProps['as'], undefined>;
  dataTestId?: string;
}
export const NextLinkComposed = forwardRef<HTMLAnchorElement, NextLinkComposedProps>(function NextLinkComposed(props, ref) {
  const {
    to,
    linkAs,
    replace,
    scroll,
    shallow,
    prefetch,
    legacyBehavior = true,
    locale,
    children,
    dataTestId,
    ...other
  } = props;
  return <NextLink href={to} prefetch={prefetch} as={linkAs} replace={replace} scroll={scroll} shallow={shallow} passHref locale={locale} legacyBehavior={legacyBehavior}>
			<Anchor ref={ref} data-testid={dataTestId || `anchor_next_link_composed_${toDisplayString(to)}`} {...other}>
				{children}
			</Anchor>
		</NextLink>;
});
export type LinkProps = {
  activeClassName?: string;
  as?: Exclude<NextLinkProps['as'], undefined>;
  href: NextLinkProps['href'];
  linkAs?: Exclude<NextLinkProps['as'], undefined>; // Useful when the as prop is shallow by styled().
  noLinkStyle?: boolean;
  openInNewTab?: boolean;
  openInNewWindow?: boolean;
  linkSx?: Exclude<MuiLinkProps['sx'], undefined>;
  dataTestId?: string;
} & Omit<NextLinkComposedProps, 'to' | 'linkAs' | 'href'> & Omit<MuiLinkProps, 'href'>;
interface ExternalLinkRenderProps {
  className?: string;
  href: string;
  noLinkStyle?: boolean;
  openInNewTab?: boolean;
  openInNewWindow?: boolean;
  linkSx?: Exclude<MuiLinkProps['sx'], undefined>;
  dataTestId?: string;
  children: React.ReactNode;
  ref: React.ForwardedRef<HTMLAnchorElement>;
  other: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;
}
const renderExternalLink = ({
  className,
  href,
  noLinkStyle,
  openInNewTab,
  openInNewWindow,
  linkSx,
  dataTestId,
  children,
  ref,
  other
}: ExternalLinkRenderProps) => {
  const shouldOpenInNewTab = openInNewTab && !openInNewWindow;
  const target = shouldOpenInNewTab ? '_blank' : '_self';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const newWindow = globalThis.window.open('', '_blank', 'width=800,height=600,noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
      newWindow.location.href = href;
    }
  };
  if (noLinkStyle) {
    return <Anchor className={className} target={target} href={href} ref={ref} {...other} data-testid={dataTestId || `anchor_external_no_link_style_${href}`} rel={rel} onClick={!openInNewTab && openInNewWindow ? handleClick : undefined}>
				{children}
			</Anchor>;
  }
  return <MuiLink className={className} target={target} href={href} ref={ref} sx={{
    ...linkSx
  }} {...other} data-testid={dataTestId || `anchor_external_${href}`} rel={rel} onClick={!openInNewTab && openInNewWindow ? handleClick : undefined} data-sentry-element="MuiLink" data-sentry-component="renderExternalLink" data-sentry-source-file="NextLinkComposed.tsx">
			{children}
		</MuiLink>;
};

// A styled version of the Next.js Link component:
// https://nextjs.org/docs/api-reference/next/link
const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  const {
    as,
    className,
    href,
    legacyBehavior,
    linkAs: linkAsProp,
    locale,
    noLinkStyle,
    prefetch,
    replace,
    scroll,
    shallow,
    openInNewTab,
    openInNewWindow,
    children,
    linkSx = {},
    dataTestId,
    ...other
  } = props;
  const otherProps = {
    ...other
  };
  delete (otherProps as {
    role?: unknown;
  }).role;
  const hrefString = toDisplayString(href);
  const {
    isExternalUrl
  } = getUrlType(hrefString);
  if (isExternalUrl) {
    const externalAnchorProps = otherProps as Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;
    return renderExternalLink({
      className,
      href: hrefString,
      noLinkStyle,
      openInNewTab,
      openInNewWindow,
      linkSx,
      dataTestId,
      children,
      ref,
      other: externalAnchorProps
    });
  }
  const linkAs = linkAsProp || as;
  const nextjsProps = {
    to: href,
    linkAs,
    replace,
    scroll,
    shallow,
    prefetch,
    legacyBehavior,
    locale
  };
  if (noLinkStyle) {
    return <NextLinkComposed className={className} ref={ref} dataTestId={dataTestId} {...nextjsProps} {...otherProps}>
				{children}
			</NextLinkComposed>;
  }
  return <MuiLink component={NextLinkComposed} className={className} ref={ref} sx={{
    ...baseAnchorStyles,
    ...linkSx
  }} dataTestId={dataTestId} {...nextjsProps} {...otherProps}>
			{children}
		</MuiLink>;
});
export const DISPLAY_NAME = 'NextLink';
Link.displayName = DISPLAY_NAME;
export default Link;